// ProjectView.tsx — Phase 3: full-width editable project properties view
// 3-column layout: form / dashboard+weather+map / settings

import { useState, useEffect } from 'react';
import { useShallowStore } from '../store/useStore';
import type { CVProject, MapView, ProjectSettings } from '../types/panelvar';
import ProjectMap from './ProjectMap';

// ── WMO weather code → description + icon ──────────────────────────────────
const WMO: Record<number, [string, string]> = {
  0: ['Clear sky', '☀️'],        1: ['Mainly clear', '🌤️'],
  2: ['Partly cloudy', '⛅'],    3: ['Overcast', '☁️'],
  45: ['Fog', '🌫️'],            48: ['Rime fog', '🌫️'],
  51: ['Light drizzle', '🌧️'],  53: ['Drizzle', '🌧️'],        55: ['Dense drizzle', '🌧️'],
  61: ['Light rain', '🌧️'],     63: ['Rain', '🌧️'],            65: ['Heavy rain', '🌧️'],
  66: ['Freezing rain', '🌨️'],  67: ['Heavy freezing rain', '🌨️'],
  71: ['Light snow', '❄️'],      73: ['Snow', '❄️'],             75: ['Heavy snow', '❄️'],
  77: ['Snow grains', '❄️'],     80: ['Light showers', '🌦️'],   81: ['Showers', '🌦️'],
  82: ['Heavy showers', '🌦️'],  85: ['Snow showers', '🌨️'],    86: ['Heavy snow showers', '🌨️'],
  95: ['Thunderstorm', '⛈️'],    96: ['T-storm w/ hail', '⛈️'],  99: ['T-storm w/ heavy hail', '⛈️'],
};

interface GeoCoords { lat: number; lng: number; display: string }
interface WeatherData {
  temperature: number; feelsLike: number; humidity: number;
  windSpeed: number; windGusts: number; windDir: number;
  precipitation: number; precipProb: number;
  weatherCode: number; isDay: boolean;
  uvIndex: number;
}
interface ForecastDay {
  date: string; dayName: string;
  tempHigh: number; tempLow: number;
  weatherCode: number;
  windMax: number; gustMax: number; windDirDom: number;
  precipSum: number; precipProb: number;
}

function useGeocode(address: string, city: string, state: string): GeoCoords | null {
  const [coords, setCoords] = useState<GeoCoords | null>(null);

  useEffect(() => {
    const query = [address, city, state].filter(Boolean).join(', ').trim();
    if (!query) return;

    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`, { signal: ctrl.signal })
        .then(r => r.json())
        .then(data => {
          if (data?.[0]) {
            const r = data[0];
            const parts = (r.display_name as string).split(',').map((s: string) => s.trim());
            setCoords({ lat: parseFloat(r.lat), lng: parseFloat(r.lon), display: parts.slice(0, 3).join(', ') });
          }
        })
        .catch(() => {});
    }, 400);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [address, city, state]);

  return coords;
}

function useWeather(coords: GeoCoords | null, refreshKey: number): { weather: WeatherData | null; forecast: ForecastDay[]; loading: boolean } {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [forecast, setForecast] = useState<ForecastDay[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!coords) return;
    setLoading(true);

    const ctrl = new AbortController();
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lng}`
      + `&current=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,weather_code,wind_speed_10m,wind_direction_10m,wind_gusts_10m,is_day,uv_index`
      + `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max,wind_speed_10m_max,wind_gusts_10m_max,wind_direction_10m_dominant`
      + `&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch&forecast_days=7`;

    fetch(url, { signal: ctrl.signal })
      .then(r => r.json())
      .then(data => {
        const c = data.current;
        setWeather({
          temperature: c.temperature_2m,
          feelsLike: c.apparent_temperature,
          humidity: c.relative_humidity_2m,
          windSpeed: c.wind_speed_10m,
          windGusts: c.wind_gusts_10m,
          windDir: c.wind_direction_10m,
          precipitation: c.precipitation,
          precipProb: data.daily?.precipitation_probability_max?.[0] ?? 0,
          weatherCode: c.weather_code,
          isDay: !!c.is_day,
          uvIndex: c.uv_index,
        });
        const d = data.daily;
        if (d?.time) {
          const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
          setForecast(d.time.map((t: string, i: number) => {
            const dt = new Date(t + 'T12:00:00');
            return {
              date: t,
              dayName: i === 0 ? 'Today' : dayNames[dt.getDay()],
              tempHigh: d.temperature_2m_max[i],
              tempLow: d.temperature_2m_min[i],
              weatherCode: d.weather_code[i],
              windMax: d.wind_speed_10m_max[i],
              gustMax: d.wind_gusts_10m_max[i],
              windDirDom: d.wind_direction_10m_dominant[i],
              precipSum: d.precipitation_sum[i],
              precipProb: d.precipitation_probability_max[i],
            };
          }));
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
    return () => ctrl.abort();
  }, [coords, refreshKey]);

  return { weather, forecast, loading };
}

function windDirection(deg: number): string {
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
}

/** Returns true if weather is unsuitable for concrete pour */
function pourWarning(w: WeatherData): string | null {
  if (w.temperature < 40) return 'Too cold for concrete placement (<40°F)';
  if (w.temperature > 95) return 'Caution: hot-weather concreting (>95°F)';
  if (w.windSpeed > 25) return 'High winds — risk of rapid surface drying';
  if (w.precipProb > 60) return `${w.precipProb}% precipitation probability`;
  if (w.precipitation > 0.1) return 'Active precipitation — protect fresh concrete';
  return null;
}

export default function ProjectView() {
  const { project, updateProject } = useShallowStore(s => ({
    project: s.project,
    updateProject: s.updateProject,
  }));

  if (!project) return null;

  function setField<K extends keyof CVProject>(key: K, value: CVProject[K]) {
    updateProject({ [key]: value } as Partial<CVProject>);
  }

  const s = project.settings;
  function setSetting<K extends keyof ProjectSettings>(key: K, value: ProjectSettings[K]) {
    updateProject({ settings: { ...s, [key]: value } });
  }

  // Geocode + weather
  const [weatherKey, setWeatherKey] = useState(0);
  const coords = useGeocode(project.address ?? '', project.city, project.state);
  const { weather, forecast, loading: weatherLoading } = useWeather(coords, weatherKey);
  const warning = weather ? pourWarning(weather) : null;

  const [showLeft, setShowLeft] = useState(true);
  const [showDash, setShowDash] = useState(true);

  return (
    <div className="project-view">
      {/* Full-bleed map background */}
      <div className="pv-map-bg">
        {coords ? (
          <ProjectMap
            coords={coords}
            savedView={project.mapView}
            onViewChange={(v: MapView) => setField('mapView', v)}
          />
        ) : (
          <div className="pv-map-placeholder">Enter an address to see map</div>
        )}
      </div>

      {/* Floating header bar */}
      <div className="pv-floating-header">
        <h2>Project: {project.name || 'Untitled'}</h2>
        <span className="pv-header-sub">
          {project.client && `Client: ${project.client}`}
          {project.address && ` · ${project.address}`}
          {coords && (
            <> · <a href={`https://www.openstreetmap.org/?mlat=${coords.lat}&mlon=${coords.lng}#map=16/${coords.lat}/${coords.lng}`} target="_blank" rel="noopener noreferrer">View larger map</a></>
          )}
        </span>
      </div>

      {/* Fixed weather bar — always visible */}
      <div className="pv-weather-bar">
        <div className="weather-widget">
          <div className="weather-header">
            <h4>Job-Site Weather</h4>
            {coords && <button className="weather-refresh" onClick={() => setWeatherKey(k => k + 1)} title="Refresh">↻</button>}
          </div>
          {!coords && (
            <p className="weather-empty">Enter a city/state to see weather conditions</p>
          )}
          {coords && weatherLoading && !weather && (
            <p className="weather-loading">Loading weather…</p>
          )}
          {coords && weather && (() => {
            const [desc, icon] = WMO[weather.weatherCode] ?? ['Unknown', '🌡️'];
            return (
              <div className="weather-bar-content">
                <div className="weather-current">
                  <span className="weather-icon">{icon}</span>
                  <div className="weather-temp-block">
                    <span className="weather-temp">{Math.round(weather.temperature)}°F</span>
                    <span className="weather-desc">{desc}</span>
                  </div>
                </div>
                <div className="weather-stats">
                  <span>Feels {Math.round(weather.feelsLike)}°</span>
                  <span>💧 {weather.humidity}%</span>
                  <span>🌬️ {Math.round(weather.windSpeed)} {windDirection(weather.windDir)}</span>
                  <span>Gusts {Math.round(weather.windGusts)}</span>
                  <span>Precip {weather.precipitation > 0 ? `${weather.precipitation}"` : '0"'}</span>
                  <span>Rain {weather.precipProb}%</span>
                  <span>UV {weather.uvIndex}</span>
                </div>
                {warning && <div className="weather-warning">⚠️ {warning}</div>}
              </div>
            );
          })()}
          {coords && weather && forecast.length > 0 && (
            <div className="forecast-grid">
              {forecast.map(day => {
                const [, fIcon] = WMO[day.weatherCode] ?? ['?', '🌡️'];
                return (
                  <div className="forecast-day" key={day.date}>
                    <span className="fd-name">{day.dayName}</span>
                    <span className="fd-icon">{fIcon}</span>
                    <span className="fd-temps">
                      <span className="fd-hi">{Math.round(day.tempHigh)}°</span>
                      <span className="fd-lo">{Math.round(day.tempLow)}°</span>
                    </span>
                    <span className="fd-wind" title={`Gusts ${Math.round(day.gustMax)} mph`}>
                      🌬️ {Math.round(day.windMax)} {windDirection(day.windDirDom)}
                    </span>
                    <span className="fd-precip">
                      💧 {day.precipProb}%
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {coords && (
            <p className="weather-attribution">
              {coords.display} · <a href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer">Open-Meteo</a>
            </p>
          )}
        </div>
      </div>

      {/* Collapsible side panels + bottom dashboard */}
      <div className="pv-overlay">
        {/* Left panel — Project Details */}
        <div className="pv-side pv-side-left">
          {showLeft && (
          <div className="pv-panel pv-panel-left">
            <h3>Project Details</h3>
          <div className="pv-form">
            <label>Project Name</label>
            <input value={project.name} onChange={e => setField('name', e.target.value)} />

            <label>Building Name</label>
            <input value={project.buildingName} onChange={e => setField('buildingName', e.target.value)} />

            <label>Job Number</label>
            <input value={project.jobNumber} onChange={e => setField('jobNumber', e.target.value)} />

            <label>Client / Contractor</label>
            <input value={project.client} onChange={e => setField('client', e.target.value)} />

            <label>Engineer</label>
            <input value={project.engineer} onChange={e => setField('engineer', e.target.value)} />

            <label>Superintendent</label>
            <input value={project.superintendent} onChange={e => setField('superintendent', e.target.value)} />

            <label>Address</label>
            <input value={project.address ?? ''} onChange={e => setField('address', e.target.value)} />

            <label>City</label>
            <input value={project.city ?? ''} onChange={e => setField('city', e.target.value)} />

            <label>State</label>
            <input value={project.state ?? ''} onChange={e => setField('state', e.target.value)} />

            <label>Phone</label>
            <input value={project.phone} onChange={e => setField('phone', e.target.value)} type="tel" />

            <label>Fax</label>
            <input value={project.fax} onChange={e => setField('fax', e.target.value)} type="tel" />

            <label>Email</label>
            <input value={project.email} onChange={e => setField('email', e.target.value)} type="email" />

            <h4>Concrete</h4>
            <label>f'c Strength (psi)</label>
            <input type="number" value={s.concreteStrength} step={500} min={0}
              onChange={e => setSetting('concreteStrength', Number(e.target.value))} />
            <label>Unit Weight (pcf)</label>
            <input type="number" value={s.concreteDensity} step={5} min={0}
              onChange={e => setSetting('concreteDensity', Number(e.target.value))} />

            <h4>Joints</h4>
            <label>Joint Width (in)</label>
            <input type="number" value={s.jointWidth} step={0.125} min={0}
              onChange={e => setSetting('jointWidth', Number(e.target.value))} />

            {s.developerMode && (<>
            <h4>Reinforcement</h4>
            <label>Rebar Size</label>
            <select value={s.rebarSize} onChange={e => setSetting('rebarSize', e.target.value)}>
              {['#3','#4','#5','#6','#7','#8','#9','#10','#11'].map(sz => (
                <option key={sz} value={sz}>{sz}</option>
              ))}
            </select>
            <label>Spacing (in o.c.)</label>
            <input type="number" value={s.rebarSpacing} step={1} min={0}
              onChange={e => setSetting('rebarSpacing', Number(e.target.value))} />
            <label>Layers</label>
            <select value={s.rebarLayers}
              onChange={e => setSetting('rebarLayers', e.target.value as 'single' | 'double')}>
              <option value="single">Single</option>
              <option value="double">Double</option>
            </select>
            <label>Clear Cover (in)</label>
            <input type="number" value={s.clearCover} step={0.125} min={0}
              onChange={e => setSetting('clearCover', Number(e.target.value))} />
            </>)}
          </div>
        </div>
          )}
          <button className="pv-collapse-tab pv-collapse-left" onClick={() => setShowLeft(v => !v)}>
            {showLeft ? '◀' : '▶'}
          </button>
        </div>

      </div>

      {/* Bottom dashboard strip — collapsible */}
      <div className="pv-bottom-strip">
        <button className="pv-collapse-tab pv-collapse-bottom" onClick={() => setShowDash(v => !v)}>
          {showDash ? '▼' : '▲'} Dashboard
        </button>
        {showDash && (
          <div className="pv-dash-bar">
            <div className="stat-card">
              <span className="stat-value">{project.panels.length}</span>
              <span className="stat-label">Panels</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{project.elevations?.length ?? 0}</span>
              <span className="stat-label">Elevations</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{project.site ? '✓' : '—'}</span>
              <span className="stat-label">Site Plan</span>
              {project.site?.layout && (
                <span className="stat-sub" title="Dual-view: TU = erected on slab, LO = cast layout">
                  {project.site.activeView === 'lo' ? 'Layout view' : 'Erected view'}
                  {' · '}
                  {project.site.layout.insertCount} cast panels
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
